import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styles from "./ProfileEdit.module.css";
import { checkToken } from "../../Utils/TokenService";
import { useNavigate } from "react-router-dom";
import config from "../../../settings/config";
import { toast } from "react-toastify";
import CustomModal from "../../Utils/CustomModal";
import Header from "../../Utils/Header/Header";
import localeRu from "../../../locales/ru/profile/profileEdit.json";
import localeEn from "../../../locales/en/profile/profileEdit.json";
import useAuthToken from "../../../hooks/useAuthToken";
import useStateBackend from "../../../hooks/useStateBackend";
import * as yup from "yup";
import leoProfanity from "leo-profanity";

const ProfileEdit = () => {
  let lang = localStorage.getItem("language") === "ru";
  const [isPin, setIsPin] = useState(localStorage.getItem("isPin") === "true");

  const nameSchema = yup
    .string()
    // .matches(/^[A-ZА-ЯЁ][a-zа-яё]+ [A-ZА-ЯЁ][a-zа-яё]+$/, lang ? localeRu.name_invalid_format : localeEn.name_invalid_format )
    .max(35, lang ? localeRu.name_max_length : localeEn.name_max_length)
    .test(
      "no-profanity",
      lang ? localeRu.name_profanity : localeEn.name_profanity,
      (value) => {
        return value ? !leoProfanity.check(value) : true;
      },
    )
    .required(lang ? localeRu.name_required : localeEn.name_required);

  const emailSchema = yup
    .string()
    .email(lang ? localeRu.email_invalid : localeEn.email_invalid) // Проверка формата email
    .required(lang ? localeRu.email_required : localeEn.email_required);

  const birthdaySchema = yup
    .date()
    .min(
      new Date("1900-01-01"),
      lang ? localeRu.birthday_min : localeEn.birthday_min,
    )
    .max(new Date(), lang ? localeRu.birthday_max : localeEn.birthday_max)
    .required(lang ? localeRu.birthday_required : localeEn.birthday_required);

  const groupedAutos = [
    {
      label: "Leapmotor",
      code: "Leapmotor",
      items: [
        { label: "C01", value: "Leapmotor С01" },
        { label: "C11", value: "Leapmotor С11" },
        { label: "C16", value: "Leapmotor С16" },
        { label: "S01", value: "Leapmotor S01" },
      ],
    },
    {
      label: "Nissan",
      code: "Nissan",
      items: [
        { label: "Ariya", value: "Nissan Ariya" },
        { label: "LEAF", value: "Nissan LEAF" },
        { label: "NV200", value: "Nissan NV200" },
      ],
    },
    {
      label: "Tesla",
      code: "Tesla",
      items: [
        { label: "Cybertruck", value: "Tesla Cybertruck" },
        { label: "Model-3", value: "Tesla Model-3" },
        { label: "Model-S", value: "Tesla Model-S" },
        { label: "Model-Y", value: "Tesla Model-Y" },
      ],
    },
    {
      label: "BMW",
      code: "BMW",
      items: [
        { label: "i3", value: "BMW i3" },
        { label: "iX", value: "BMW iX" },
        { label: "i4", value: "BMW i4" },
      ],
    },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user);

  const [name, setName] = useState(user?.name);
  const [errorName, setErrorName] = useState("");
  const [email, setEmail] = useState(user?.email);
  const [errorEmail, setErrorEmail] = useState("");
  const [birthday, setBirthday] = useState(user?.birthday);
  const [errorBirthday, setErrorBirthday] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [customCar, setCustomCar] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [carInputError, setCarInputError] = useState("");

  useAuthToken();
  useStateBackend();

  useEffect(() => {
    if (user?.carBrand && user?.carModel) {
      const fullCar = `${user.carBrand} ${user.carModel}`;

      // Проверяем, входит ли значение в список predefined машин
      const allCars = groupedAutos?.flatMap((group) =>
        group.items.map((item) => item.value),
      );
      if (allCars.includes(fullCar)) {
        setCustomCar(fullCar);
        setIsCustom(false);
      } else {
        setCustomCar(fullCar);
        setIsCustom(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (
      errorName === "" &&
      name !== "" &&
      errorEmail === "" &&
      email !== "" &&
      errorBirthday === "" &&
      !carInputError
    ) {
      setIsButtonEnabled(true);
    } else {
      setIsButtonEnabled(false);
    }
  }, [errorEmail, errorName, email, name, errorBirthday, carInputError]);

  const handleChangeName = async (e) => {
    const value = e.target.value;
    setName(value);

    try {
      await nameSchema.validate(value);
      setErrorName("");
    } catch (err) {
      setErrorName(err.message);
    }
  };

  const handleChangeEmail = async (e) => {
    const value = e.target.value;
    setEmail(value);

    try {
      await emailSchema.validate(value);
      setErrorEmail("");
    } catch (err) {
      setErrorEmail(err.message);
    }
  };

  const handleChangeBirthday = async (e) => {
    const value = e.target.value;
    setBirthday(value);

    try {
      await birthdaySchema.validate(new Date(value));
      setErrorBirthday("");
    } catch (err) {
      setErrorBirthday(err.message);
    }
  };

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "custom") {
      setIsCustom(true);
      setCustomCar("");
    } else {
      setIsCustom(false);
      setCustomCar(selectedValue);
    }
  };

  async function requestEditUser() {
    let carBrand1 = "";
    let carModel1 = "";
    const customCarSplit = customCar.split(" ");
    if (customCarSplit.length > 1) {
      carBrand1 = customCarSplit[0];
      carModel1 = customCarSplit[1];
    } else {
      carBrand1 = customCar;
    }

    let updatedUser = {
      ...user, // Берем все старые поля
      name: name,
      email: email,
      birthday: birthday,
      carBrand: carBrand1,
      carModel: carModel1,
    };

    try {
      const response = await fetch(`${config.app.host}/api/v1/user/profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("m_token")}`,
        },
        body: JSON.stringify(updatedUser),
      });

      const responseData = await response.json();

      if (!response.ok) {
        const errorMessage = responseData?.message || "Неизвестная ошибка";
        toast.error(lang ? localeRu.error_occurred : localeEn.error_occurred, {
          position: "top-center",
          autoClose: 3000,
          style: { color: "white", backgroundColor: "black" },
        });
      } else {
        toast.success(
          lang ? localeRu.success_data_saved : localeEn.success_data_saved,
          {
            position: "top-center",
            autoClose: 3000,
            style: { color: "white", backgroundColor: "black" },
          },
        );
      }
      navigate("/profile");
    } catch (error) {}
  }

  // Обработка удаления профиля
  const confirmDeleteProfile = async () => {
    // Выполняем логику выхода, например, очищаем токены и перенаправляем на страницу входа
    let mToken = localStorage.getItem("m_token");
    let resp = "";

    if (mToken) {
      try {
        const response = await fetch(`${config.app.host}/api/v1/user/profile`, {
          method: "DELETE",
          mode: "cors",
          headers: {
            Authorization: `Bearer ${mToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          // throw new Error('Network response was not ok');
        }

        resp = await response.json();
        if (resp.error) {
          localStorage.clear();
          window.location.href = "/"; // Перенаправляем на страницу входа
        }
      } catch (error) {
        localStorage.clear();
        window.location.href = "/"; // Перенаправляем на страницу входа
      }
      toast.success(
        lang ? localeRu.profile_deleted : localeEn.profile_deleted,
        {
          position: "top-center",
          autoClose: 3000,
          style: { color: "white", backgroundColor: "black" },
        },
      );
      localStorage.clear();
      window.location.href = "/"; // Перенаправляем на страницу входа
    }
  };

  const handleChange = (e) => {};

  const formatDate = (dateString) => {
    const date = dateString || "";
    const [year, month, day] = date.split("-");
    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  const handleSaveSettings = () => {
    if (!isButtonEnabled) {
      return;
    }
    requestEditUser();
  };

  const handleIsPin = () => {
    const newIsPin = !isPin;
    setIsPin(newIsPin);
    localStorage.setItem("isPin", newIsPin);
  };

  const handleLogOut = () => {
    setShowModal(true);
  };

  return (
    <div className={styles.container}>
      <Header back={true} title={lang ? localeRu.title : localeEn.title} />
      <div className={styles.mainSection}>
        <p>{lang ? localeRu.welcome_message : localeEn.welcome_message}</p>
        <p className={styles.requiredNote}>
          {lang ? localeRu.required_fields : localeEn.required_fields}
        </p>
        <div className={styles.parameter}>
          <p className={styles.paramText}>
            {lang ? localeRu.name : localeEn.name}
          </p>
        </div>
        <input
          type="text"
          className={
            errorName === "" && name !== "" ? styles.cardOn : styles.card
          }
          value={name}
          onChange={handleChangeName}
          placeholder={
            lang ? localeRu.name_placeholder : localeEn.name_placeholder
          }
        />
        {errorName && <p style={{ color: "red" }}>{errorName}</p>}
        <div className={styles.parameter}>
          <p className={styles.paramText}>
            {lang ? localeRu.email : localeEn.email}
          </p>
        </div>
        <input
          type="text"
          className={
            errorEmail === "" && email !== "" ? styles.cardOn : styles.card
          }
          value={email}
          onChange={handleChangeEmail}
          placeholder={
            lang ? localeRu.email_placeholder : localeEn.email_placeholder
          }
        />
        {errorEmail && <p style={{ color: "red" }}>{errorEmail}</p>}
        <div className={styles.parameter}>
          <p className={styles.paramText}>
            {lang ? localeRu.birthday : localeEn.birthday}
          </p>
        </div>
        <input
          type="date"
          className={styles.card}
          value={birthday}
          placeholder={
            lang ? localeRu.date_placeholder : localeEn.date_placeholder
          }
          onFocus={(e) => (e.target.type = "date")}
          onBlur={(e) => {
            if (!e.target.value) e.target.type = "text";
          }}
          onChange={handleChangeBirthday}
        />
        {errorBirthday && <p style={{ color: "red" }}>{errorBirthday}</p>}

        <div className={styles.formGroup}>
          <label htmlFor="carModel">
            {lang ? localeRu.car_label : localeEn.car_label}
          </label>

          <select
            id="carModel"
            name="carModel"
            value={isCustom ? "custom" : customCar}
            onChange={handleSelectChange}
            className={customCar ? styles.card : styles.cardOff}
          >
            <option value="">
              {lang ? localeRu.car_not_selected : localeEn.car_not_selected}
            </option>
            {groupedAutos.map((group) => (
              <optgroup key={group.code} label={group.label}>
                {group.items.map((car) => (
                  <option key={car.value} value={car.value}>
                    {car.value}
                  </option>
                ))}
              </optgroup>
            ))}
            <option value="custom">
              {lang ? localeRu.car_other : localeEn.car_other}
            </option>
          </select>

          {isCustom && (
            <>
              <input
                type="text"
                placeholder={
                  lang ? localeRu.car_placeholder : localeEn.car_placeholder
                }
                value={customCar}
                className={styles.card}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomCar(value);

                  const wordCount = value.trim().split(/\s+/).length;
                  if (wordCount === 2) {
                    setCarInputError("");
                    handleChange({ target: { name: "carModel", value } });
                  } else {
                    setCarInputError(
                      lang
                        ? "Введите марку и модель (ровно 2 слова)"
                        : "Enter brand and model (exactly 2 words)",
                    );
                    handleChange({ target: { name: "carModel", value: "" } });
                  }
                }}
              />
              {carInputError && <p className={styles.error}>{carInputError}</p>}
            </>
          )}
        </div>

        <div className={styles.switch}>
          <p className={styles.textAutoCharge}>
            {lang ? localeRu.setPin : localeEn.setPin}
          </p>
          <div
            className={
              isPin ? styles.autoChargeSettingOn : styles.autoChargeSetting
            }
            onClick={handleIsPin}
          >
            <div className={styles.autoChargeSettingElement}></div>
          </div>
        </div>
      </div>

      <div className={styles.sectionButtons}>
        <button
          type="button"
          onClick={handleSaveSettings}
          className={
            isButtonEnabled
              ? styles.submitStartCharging
              : styles.submitStartChargingDis
          }
        >
          <p className={styles.textButtons}>
            {lang ? localeRu.save : localeEn.save}
          </p>
        </button>
        <button
          type="button"
          onClick={handleLogOut}
          className={styles.submitDelete}
        >
          <p className={styles.textButtons}>
            {lang ? localeRu.deleteAccount : localeEn.deleteAccount}
          </p>
        </button>
      </div>
      <CustomModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={confirmDeleteProfile}
        textMsg={
          lang ? localeRu.deleteConfirmTitle : localeEn.deleteConfirmTitle
        }
        textQuestions={
          lang ? localeRu.deleteConfirmText : localeEn.deleteConfirmText
        }
        yes={lang ? "Да" : "Yes"}
        no={lang ? "Нет" : "No"}
      />
    </div>
  );
};

export default ProfileEdit;
